import { releases } from '@kirin/api-client/contracts';

/**
 * KIR-4071: match a download URL to its published SHA-256 hash, and flatten the
 * manifest into the full checksum list the Install tab shows for EDR
 * allow-listing.
 *
 * The manifest keys hashes by the exact distributed filename (e.g.
 * `Kirin-3.75.5.pkg`), version by version, newest-first. A download button's
 * URL ends in that same filename, so we match by filename — the file the user
 * actually downloads, not a platform guess.
 */

/** One installer file's checksum for the aggregated list. */
export interface InstallerChecksumRow {
  file: string;
  sha256: string;
  version: string;
}

/**
 * Whether `file` is a standalone installer we surface for allow-listing. Lives
 * in the shared contract so `/api/v1` agent releases lists the same files
 * (KIR-5145).
 */
export const isInstallerFile = releases.isInstallerFile;

/** Last path segment of a URL, minus any query/hash. `null` if not derivable. */
export function filenameFromUrl(url: string): string | null {
  if (!url) return null;
  const withoutQuery = url.split(/[?#]/)[0] ?? '';
  const segment = withoutQuery.split('/').pop() ?? '';
  return segment.length > 0 ? segment : null;
}

/**
 * Resolve the SHA-256 for one installer download URL against the published
 * hashes — the digest of exactly the file this URL points at. Fail-safe: an
 * unmatched file / null manifest → `null` so nothing renders on that button.
 */
export function resolveInstallerHash(
  url: string,
  hashes: releases.InstallerHashes | null | undefined
): string | null {
  if (!hashes) return null;
  const filename = filenameFromUrl(url);
  if (!filename) return null;
  for (const entry of hashes.versions) {
    const sha256 = entry.artifacts[filename];
    if (sha256) return sha256;
  }
  return null;
}

/**
 * Flatten every published installer into a single list for the aggregated
 * checksums popout: one row per (version, installer file), newest version first
 * and filename within. `.vsix` packages are excluded (see {@link isInstallerFile}).
 * Fail-safe: null / empty manifest → `[]`.
 */
export function flattenInstallerChecksums(
  hashes: releases.InstallerHashes | null | undefined
): InstallerChecksumRow[] {
  if (!hashes) return [];
  // Producer publishes versions newest-first; preserve that, sort filename within.
  return hashes.versions.flatMap(v =>
    Object.entries(v.artifacts)
      .filter(([file]) => isInstallerFile(file))
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([file, sha256]) => ({ file, sha256, version: v.version }))
  );
}
